import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AIInput } from '@/components/ui/ai-input';
import { AITextarea } from '@/components/ui/ai-textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  MarketingActivity,
  ActivityChecklist,
  ActivityComment,
  ActivityType,
  ActivityStatus,
  PriorityLevel,
  Campaign,
  MarketingPhase,
  Supplier,
} from '@/types';
import type { ApiResponse } from '@/types';
import {
  cn,
  formatCurrency,
  formatDate,
  statusColors,
  priorityColors,
  getInitials,
} from '@/lib/utils';
import { api } from '@/lib/api';
import {
  ArrowLeft,
  Pencil,
  Download,
  Loader2,
  Save,
  Send,
  Plus,
  CheckCircle2,
  Circle,
  FileText,
  Target,
  Cog,
  BarChart3,
  MessageSquare,
  ClipboardList,
  ExternalLink,
  Building2,
  User,
  Users,
  Calendar,
  ChevronDown,
  Info,
  X,
} from 'lucide-react';

interface ActivityDetailPageProps {
  activityId: string;
  onBack: () => void;
  onNavigate: (path: string) => void;
}

const TYPE_LABELS: Record<string, string> = {
  google_ads: 'Google Ads',
  linkedin_ads: 'LinkedIn Ads',
  print: 'Print',
  email: 'E-Mail',
  campaign: 'Kampagne',
  social: 'Social',
  event: 'Event',
  content: 'Content',
  seo: 'SEO',
  webinar: 'Webinar',
  other: 'Sonstiges',
};

const STATUS_LABELS: Record<string, string> = {
  backlog: 'Backlog',
  todo: 'To-Do',
  in_progress: 'In Bearbeitung',
  review: 'Review',
  done: 'Erledigt',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  critical: 'Kritisch',
};

/** Lifts cards off the canvas in dark mode (depth + edge definition). */
const DETAIL_SURFACE_CARD =
  'border-border/90 bg-card shadow-md shadow-slate-200/40 dark:border-border/45 dark:bg-card/85 dark:shadow-[0_18px_50px_-20px_rgba(0,0,0,0.75)] dark:ring-1 dark:ring-white/[0.07]';

function detailCard(extra?: string) {
  return cn(DETAIL_SURFACE_CARD, extra);
}

const TAB_TRIGGER_DETAIL =
  'gap-2 rounded-none border-0 border-b-2 border-transparent bg-transparent px-3 py-3 text-sm font-medium text-muted shadow-none ring-offset-background transition-colors hover:bg-card-hover/90 hover:text-foreground data-[state=active]:border-border data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none dark:hover:bg-card-hover/60 dark:data-[state=active]:border-border dark:data-[state=active]:bg-transparent dark:data-[state=active]:text-muted dark:data-[state=active]:shadow-none sm:px-4';

export function ActivityDetailPage({
  activityId,
  onBack,
  onNavigate,
}: ActivityDetailPageProps) {
  const [activity, setActivity] = useState<MarketingActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // PDF export
  const [exportingPdf, setExportingPdf] = useState(false);
  const [includeChecklistsInPdf, setIncludeChecklistsInPdf] = useState(true);
  const [includeCommentsInPdf, setIncludeCommentsInPdf] = useState(true);
  const [pdfPopoverOpen, setPdfPopoverOpen] = useState(false);
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Checklist
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [addingChecklist, setAddingChecklist] = useState(false);

  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Inline field editing (Zielsetzung, Durchführung tabs)
  const [goalDraft, setGoalDraft] = useState('');
  const [targetAudienceDraft, setTargetAudienceDraft] = useState('');
  const [executionPlanDraft, setExecutionPlanDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [savingTargetAudience, setSavingTargetAudience] = useState(false);
  const [savingExecution, setSavingExecution] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);

  // KPI inline editing
  const [kpiEditing, setKpiEditing] = useState(false);
  const [kpiForm, setKpiForm] = useState({
    budget_actual: '0',
    hours_actual: '0',
    actual_leads: '0',
    actual_conversions: '0',
    actual_revenue: '0',
  });
  const [savingKpis, setSavingKpis] = useState(false);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phases, setPhases] = useState<MarketingPhase[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    type: 'other' as ActivityType,
    status: 'backlog' as ActivityStatus,
    priority: 'medium' as PriorityLevel,
    start_date: '',
    end_date: '',
    budget_planned: '0',
    budget_supplier: '0',
    hours_planned: '0',
    expected_leads: '0',
    expected_conversions: '0',
    expected_revenue: '0',
    campaign_id: null as string | null,
    phase_id: null as string | null,
    supplier_id: null as string | null,
    external_link: '',
  });

  const activityLoadRequest = useRef(0);
  const loadActivity = useCallback(async () => {
    const request = ++activityLoadRequest.current;
    setLoading(true);
    setNotFound(false);
    setPhases([]);
    try {
      const res = await api.get<ApiResponse<MarketingActivity>>(
        `/activities/${activityId}`
      );
      const loaded = res.data ?? null;
      if (request !== activityLoadRequest.current) return;
      if (!loaded) {
        setNotFound(true);
        return;
      }
      setActivity(loaded);
      setGoalDraft(loaded.goal ?? '');
      setTargetAudienceDraft(loaded.target_audience ?? '');
      setExecutionPlanDraft(loaded.execution_plan ?? '');
      setDescriptionDraft(loaded.description ?? loaded.description_md ?? '');
      setKpiForm({
        budget_actual: String(loaded.budget_actual ?? 0),
        hours_actual: String(loaded.hours_actual ?? 0),
        actual_leads: String(loaded.actual_leads ?? 0),
        actual_conversions: String(loaded.actual_conversions ?? 0),
        actual_revenue: String(loaded.actual_revenue ?? 0),
      });
      if (loaded.plan_id) {
        void api
          .get<ApiResponse<{ phases?: MarketingPhase[] }>>(
            `/marketing-plans/${loaded.plan_id}`
          )
          .then((planRes) => {
            if (request === activityLoadRequest.current) setPhases(Array.isArray(planRes.data?.phases) ? planRes.data.phases : []);
          })
          .catch(() => { if (request === activityLoadRequest.current) setPhases([]); });
      }
    } catch {
      if (request === activityLoadRequest.current) setNotFound(true);
    } finally {
      if (request === activityLoadRequest.current) setLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    void loadActivity();
    return () => { activityLoadRequest.current += 1; };
  }, [loadActivity]);

  /** Rich context so field KI stays concrete (PDF / supplier funding), not generic. */
  const activityAiContext = useMemo(() => {
    if (!activity) return '';
    const supplierName = activity.supplier?.name?.trim();
    const parts: string[] = [
      'Unternehmen: Leftclick AG.',
      `Aktivität: ${activity.title}`,
      `Kanaltyp: ${TYPE_LABELS[activity.type] ?? activity.type}`,
      `Priorität: ${PRIORITY_LABELS[activity.priority] ?? activity.priority}`,
      `Status: ${STATUS_LABELS[activity.status] ?? activity.status}`,
    ];
    if (activity.budget_planned > 0) {
      parts.push(`Geplantes Budget: ${formatCurrency(activity.budget_planned)}`);
    }
    if (activity.start_date || activity.end_date) {
      const range = [activity.start_date, activity.end_date].filter(Boolean).join(' – ');
      if (range) parts.push(`Zeitraum: ${range}`);
    }
    if (supplierName) parts.push(`Lieferant: ${supplierName}`);
    parts.push(
      'Verwendung: PDF-Export und Förderanträge bei Lieferanten — Text muss sachlich, präzise und ohne Worthülsen sein.'
    );
    return parts.join(' ');
  }, [activity]);

  // Load PDF defaults
  useEffect(() => {
    void api
      .get<
        ApiResponse<{
          include_checklists_default: boolean;
          include_comments_default: boolean;
        }>
      >('/export/pdf-settings')
      .then((res) => {
        if (typeof res.data?.include_checklists_default === 'boolean')
          setIncludeChecklistsInPdf(res.data.include_checklists_default);
        if (typeof res.data?.include_comments_default === 'boolean')
          setIncludeCommentsInPdf(res.data.include_comments_default);
      })
      .catch(() => undefined);
  }, []);

  // ── PDF Export ──────────────────────────────────────────────────────────────

  const handleExportPdf = async () => {
    if (!activity) return;
    setPdfPopoverOpen(false);
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({
        include_checklists: includeChecklistsInPdf ? '1' : '0',
        include_comments: includeCommentsInPdf ? '1' : '0',
      });
      const blob = await api.getBlob(
        `/export/activity/${activity.id}/pdf?${params.toString()}`
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-${activity.id}-detail.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF erfolgreich erstellt.');
    } catch {
      toast.error('PDF Export fehlgeschlagen.');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleBriefingDownload = async () => {
    if (!activity) return;
    setBriefingLoading(true);
    try {
      const blob = await api.getBlob(`/activities/${activity.id}/briefing`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `briefing-${activity.id.slice(0, 8)}.md`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Briefing (Markdown) heruntergeladen.');
    } catch {
      toast.error('Briefing konnte nicht heruntergeladen werden.');
    } finally {
      setBriefingLoading(false);
    }
  };

  // ── Checkliste ──────────────────────────────────────────────────────────────

  const handleChecklistToggle = async (item: ActivityChecklist) => {
    if (!activity) return;
    try {
      await api.patch<ApiResponse<ActivityChecklist>>(
        `/activities/${activity.id}/checklists/${item.id}`,
        { is_completed: !item.is_completed }
      );
      void loadActivity();
    } catch {
      toast.error('Konnte Checklistenpunkt nicht aktualisieren.');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!activity || !newChecklistTitle.trim()) return;
    setAddingChecklist(true);
    try {
      await api.post<ApiResponse<ActivityChecklist>>(
        `/activities/${activity.id}/checklists`,
        {
          title: newChecklistTitle.trim(),
          sort_order: activity.checklists?.length ?? 0,
        }
      );
      setNewChecklistTitle('');
      void loadActivity();
    } catch {
      toast.error('Checklistenpunkt konnte nicht hinzugefügt werden.');
    } finally {
      setAddingChecklist(false);
    }
  };

  // ── Kommentare ──────────────────────────────────────────────────────────────

  const handleAddComment = async () => {
    if (!activity || !newComment.trim()) return;
    setAddingComment(true);
    try {
      await api.post<ApiResponse<ActivityComment>>(
        `/activities/${activity.id}/comments`,
        { content: newComment.trim() }
      );
      setNewComment('');
      void loadActivity();
    } catch {
      toast.error('Kommentar konnte nicht gespeichert werden.');
    } finally {
      setAddingComment(false);
    }
  };

  // ── Inline-Speichern ────────────────────────────────────────────────────────

  const patchActivity = async (fields: Record<string, unknown>) => {
    if (!activity) return;
    await api.patch<ApiResponse<MarketingActivity>>(
      `/activities/${activity.id}`,
      fields
    );
    void loadActivity();
  };

  const handleSaveGoal = async () => {
    setSavingGoal(true);
    try {
      await patchActivity({ goal: goalDraft.trim() || null, goal_md: goalDraft.trim() || null });
      toast.success('Zielsetzung gespeichert.');
    } catch {
      toast.error('Zielsetzung konnte nicht gespeichert werden.');
    } finally {
      setSavingGoal(false);
    }
  };

  const handleSaveTargetAudience = async () => {
    setSavingTargetAudience(true);
    try {
      await patchActivity({ target_audience: targetAudienceDraft.trim() || null });
      toast.success('Zielgruppe gespeichert.');
    } catch {
      toast.error('Zielgruppe konnte nicht gespeichert werden.');
    } finally {
      setSavingTargetAudience(false);
    }
  };

  const handleSaveExecution = async () => {
    setSavingExecution(true);
    try {
      await patchActivity({
        execution_plan: executionPlanDraft.trim() || null,
        execution_plan_md: executionPlanDraft.trim() || null,
      });
      toast.success('Durchführungsplan gespeichert.');
    } catch {
      toast.error('Durchführungsplan konnte nicht gespeichert werden.');
    } finally {
      setSavingExecution(false);
    }
  };

  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      await patchActivity({
        description: descriptionDraft.trim() || null,
        description_md: descriptionDraft.trim() || null,
      });
      toast.success('Beschreibung gespeichert.');
    } catch {
      toast.error('Beschreibung konnte nicht gespeichert werden.');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleSaveKpis = async () => {
    setSavingKpis(true);
    try {
      await patchActivity({
        budget_actual: Number(kpiForm.budget_actual),
        hours_actual: Number(kpiForm.hours_actual),
        actual_leads: Number(kpiForm.actual_leads),
        actual_conversions: Number(kpiForm.actual_conversions),
        actual_revenue: Number(kpiForm.actual_revenue),
      });
      setKpiEditing(false);
      toast.success('KPIs gespeichert.');
    } catch {
      toast.error('KPIs konnten nicht gespeichert werden.');
    } finally {
      setSavingKpis(false);
    }
  };

  // ── Edit-Dialog ─────────────────────────────────────────────────────────────

  const openEditDialog = () => {
    if (!activity) return;
    setEditError(null);
    setEditForm({
      title: activity.title,
      description: activity.description ?? activity.description_md ?? '',
      type: activity.type,
      status: activity.status,
      priority: activity.priority,
      start_date: activity.start_date ?? '',
      end_date: activity.end_date ?? '',
      budget_planned: String(activity.budget_planned ?? 0),
      budget_supplier: String(activity.budget_supplier ?? 0),
      hours_planned: String(activity.hours_planned ?? 0),
      expected_leads: String(activity.expected_leads ?? 0),
      expected_conversions: String(activity.expected_conversions ?? 0),
      expected_revenue: String(activity.expected_revenue ?? 0),
      campaign_id: activity.campaign_id ?? null,
      phase_id: activity.phase_id ?? null,
      supplier_id: activity.supplier_id ?? null,
      external_link: activity.external_link ?? '',
    });
    // Load campaigns, suppliers
    if (activity.plan_id) {
      void api
        .get<ApiResponse<Campaign[]>>(
          `/campaigns?plan_id=${activity.plan_id}&limit=200`
        )
        .then((res) =>
          setCampaigns(Array.isArray(res.data) ? res.data : [])
        )
        .catch(() => setCampaigns([]));
    }
    void api
      .get<ApiResponse<Supplier[]>>('/suppliers?limit=200&is_active=true')
      .then((res) =>
        setSuppliers(Array.isArray(res.data) ? res.data : [])
      )
      .catch(() => setSuppliers([]));
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!activity) return;
    setEditError(null);
    if (!editForm.title.trim()) {
      setEditError('Bitte einen Titel eingeben.');
      return;
    }
    if (
      editForm.end_date &&
      editForm.start_date &&
      editForm.end_date < editForm.start_date
    ) {
      setEditError('Das Enddatum muss nach dem Startdatum liegen.');
      return;
    }
    const budgetPlanned = Number(editForm.budget_planned);
    const budgetSupplier = Number(editForm.budget_supplier);
    const hoursPlanned = Number(editForm.hours_planned);
    const expectedLeads = Number(editForm.expected_leads);
    const expectedConversions = Number(editForm.expected_conversions);
    const expectedRevenue = Number(editForm.expected_revenue);

    if (
      [budgetPlanned, budgetSupplier, hoursPlanned, expectedLeads, expectedConversions, expectedRevenue].some(
        (v) => Number.isNaN(v) || v < 0
      )
    ) {
      setEditError('Alle Zahlenfelder müssen gültige positive Werte sein.');
      return;
    }
    setSaving(true);
    try {
      await api.patch<ApiResponse<MarketingActivity>>(
        `/activities/${activity.id}`,
        {
          title: editForm.title.trim(),
          description: editForm.description.trim() || null,
          description_md: editForm.description.trim() || null,
          type: editForm.type,
          status: editForm.status,
          priority: editForm.priority,
          start_date: editForm.start_date || null,
          end_date: editForm.end_date || null,
          budget_planned: budgetPlanned,
          budget_supplier: budgetSupplier,
          hours_planned: hoursPlanned,
          expected_leads: expectedLeads,
          expected_conversions: expectedConversions,
          expected_revenue: expectedRevenue,
          campaign_id: editForm.campaign_id || null,
          phase_id: editForm.phase_id || null,
          supplier_id: editForm.supplier_id || null,
          external_link: editForm.external_link.trim() || null,
        }
      );
      setEditDialogOpen(false);
      await loadActivity();
      toast.success('Aktivität gespeichert.');
    } catch (err) {
      setEditError(
        err instanceof Error
          ? err.message
          : 'Aktivität konnte nicht gespeichert werden.'
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Hilfsfunktionen ─────────────────────────────────────────────────────────

  const goalPct =
    activity && activity.expected_leads > 0
      ? Math.min(
          100,
          Math.round((activity.actual_leads / activity.expected_leads) * 100)
        )
      : 0;

  const budgetPct =
    activity && activity.budget_planned > 0
      ? Math.min(
          100,
          Math.round((activity.budget_actual / activity.budget_planned) * 100)
        )
      : 0;

  const checklistDone =
    activity?.checklists?.filter((c) => c.is_completed).length ?? 0;
  const checklistTotal = activity?.checklists?.length ?? 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-full flex-col p-6">
        <div className="mb-4 h-8 w-48 animate-pulse rounded bg-card-hover" />
        <div className="mb-2 h-10 w-3/4 animate-pulse rounded bg-card-hover" />
        <div className="mt-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded bg-card-hover"
            />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !activity) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <p className="text-lg text-muted">Aktivität nicht gefunden.</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border/90 bg-card shadow-[0_1px_0_rgba(15,23,42,0.04)] dark:border-border/40 dark:bg-background/90 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04),0_20px_50px_-28px_rgba(0,0,0,0.55)]">
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-10 border-b border-border/90 bg-card/95 px-5 py-5 backdrop-blur-md dark:border-border/50 dark:bg-background/90 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
            <Button
              variant="outline"
              size="icon"
              className="mt-0.5 h-9 w-9 shrink-0 rounded-lg border-border bg-background text-muted hover:bg-card-hover dark:bg-card dark:text-foreground"
              onClick={onBack}
              aria-label="Zurück zur Aktivitätenliste"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-balance text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                {activity.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge className={cn(statusColors[activity.status])}>
                  {STATUS_LABELS[activity.status] ?? activity.status}
                </Badge>
                <Badge className={cn(priorityColors[activity.priority])}>
                  {PRIORITY_LABELS[activity.priority] ?? activity.priority}
                </Badge>
                <Badge variant="secondary" className="font-normal">
                  {TYPE_LABELS[activity.type] ?? activity.type}
                </Badge>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted">
                Planen und dokumentieren Sie diese Aktivität in den Tabs. Export und Bearbeitung sind rechts verfügbar.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            {/* PDF Popover */}
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Popover open={pdfPopoverOpen} onOpenChange={setPdfPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        className="shadow-sm"
                        disabled={exportingPdf}
                      >
                        {exportingPdf ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        PDF exportieren
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <p className="mb-2 text-xs font-medium text-muted">
                  PDF Optionen
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={includeChecklistsInPdf}
                      onCheckedChange={(v) =>
                        setIncludeChecklistsInPdf(Boolean(v))
                      }
                    />
                    Checkliste einbeziehen
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={includeCommentsInPdf}
                      onCheckedChange={(v) =>
                        setIncludeCommentsInPdf(Boolean(v))
                      }
                    />
                    Kommentare einbeziehen
                  </label>
                </div>
                <Button
                  className="mt-3 w-full"
                  size="sm"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                >
                  {exportingPdf ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  PDF erstellen
                </Button>
              </PopoverContent>
                  </Popover>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm">
                Exportiert eine PDF-Zusammenfassung. Vor dem Erstellen können Sie Checkliste und Kommentare ein- oder
                ausblenden.
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="border-border bg-card-hover text-foreground hover:bg-border"
                    disabled={briefingLoading}
                    onClick={() => void handleBriefingDownload()}
                  >
                    {briefingLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Briefing (.md)
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm">
                Strukturiertes Markdown-Briefing für Zulieferer oder Dokumentation (Ziel, Beschreibung, Budget).
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-border bg-card hover:bg-background dark:hover:bg-card-hover"
                    onClick={openEditDialog}
                  >
                    <Pencil className="h-4 w-4" />
                    Bearbeiten
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-sm" side="bottom">
                Titel, Status, Budget, Zuweisung, Termine und Verknüpfungen im Dialog ändern.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex min-h-0 flex-1 flex-col">
        <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-border bg-background/80 px-3 dark:border-border/50 dark:bg-card/50 sm:px-5">
            <TabsList className="h-auto min-h-0 w-full flex-wrap justify-start gap-0 rounded-none bg-transparent p-0">
              {(
                [
                  {
                    value: 'overview',
                    icon: FileText,
                    label: 'Übersicht',
                    hint: 'Plan, Phase, Zuweisung, Fristen — kompakte Metadaten.',
                  },
                  {
                    value: 'goal',
                    icon: Target,
                    label: 'Zielsetzung',
                    hint: 'Zweck und messbare Ziele dieser Aktivität.',
                  },
                  {
                    value: 'execution',
                    icon: Cog,
                    label: 'Durchführung',
                    hint: 'Umsetzung: Links, Schritte und operative Hinweise.',
                  },
                  {
                    value: 'checklist',
                    icon: ClipboardList,
                    label: `Checkliste${checklistTotal > 0 ? ` (${checklistDone}/${checklistTotal})` : ''}`,
                    hint: 'Teilaufgaben abhaken; Fortschritt ist für das Team sichtbar.',
                  },
                  {
                    value: 'comments',
                    icon: MessageSquare,
                    label: 'Kommentare',
                    hint: 'Abstimmung und Entscheidungen — besser als verstreute E-Mails.',
                  },
                  {
                    value: 'kpis',
                    icon: BarChart3,
                    label: 'KPIs & Budget',
                    hint: 'Geplantes und effektives Budget, Leads, Stunden — für Auswertungen pflegen.',
                  },
                ]
              ).map(({ value, icon: Icon, label, hint }) => (
                <Tooltip key={value} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <TabsTrigger value={value} className={TAB_TRIGGER_DETAIL}>
                      <Icon className="h-4 w-4" />
                      {label}
                    </TabsTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-sm">
                    {hint}
                  </TooltipContent>
                </Tooltip>
              ))}
            </TabsList>
          </div>

          <ScrollArea className="h-[calc(100vh-13rem)] min-h-[280px] bg-gradient-to-b from-background/60 to-white dark:from-background/90 dark:to-card/35">
            {/* ── Tab: Übersicht ── */}
            <TabsContent value="overview" className="m-0 p-6">
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Metadaten */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <FileText className="h-4 w-4" />
                      Metadaten
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="text-muted">Marketingplan</span>
                      <span className="font-medium">
                        {(activity as any).marketing_plans?.title ??
                          activity.plan_id}
                        {(activity as any).marketing_plans?.year
                          ? ` (${(activity as any).marketing_plans.year})`
                          : ''}
                      </span>
                    </div>
                    {activity.phase_id && (
                      <div className="grid grid-cols-[140px_1fr] gap-1">
                        <span className="text-muted">Phase / Quartal</span>
                        <span className="font-medium">
                          {(activity as any).marketing_phases?.name ??
                            phases.find((p) => p.id === activity.phase_id)
                              ?.name ??
                            activity.phase_id}
                        </span>
                      </div>
                    )}
                    {activity.campaign_id && (
                      <div className="grid grid-cols-[140px_1fr] gap-1">
                        <span className="text-muted">Kampagne</span>
                        <button
                          type="button"
                          className="text-left font-medium text-accent hover:underline"
                          onClick={() =>
                            onNavigate(`/campaigns/${activity.campaign_id}`)
                          }
                        >
                          {campaigns.find(
                            (c) => c.id === activity.campaign_id
                          )?.name ?? activity.campaign_id}
                        </button>
                      </div>
                    )}
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="flex items-center gap-1 text-muted">
                        <User className="h-3 w-3" />
                        Zugewiesen
                      </span>
                      <span className="font-medium">
                        {activity.assignee?.full_name ??
                          (activity as any).users?.full_name ??
                          '—'}
                      </span>
                    </div>
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="flex items-center gap-1 text-muted">
                        <Users className="h-3 w-3" />
                        Team
                      </span>
                      <span className="font-medium">
                        {activity.team?.name ??
                          (activity as any).teams?.name ??
                          '—'}
                      </span>
                    </div>
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="flex items-center gap-1 text-muted">
                        <Calendar className="h-3 w-3" />
                        Zeitraum
                      </span>
                      <span className="font-medium">
                        {activity.start_date
                          ? formatDate(activity.start_date)
                          : '—'}{' '}
                        –{' '}
                        {activity.end_date
                          ? formatDate(activity.end_date)
                          : '—'}
                      </span>
                    </div>
                    {activity.external_link && (
                      <div className="grid grid-cols-[140px_1fr] gap-1">
                        <span className="text-muted">Externe URL</span>
                        <a
                          href={activity.external_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 font-medium text-accent hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Link öffnen
                        </a>
                      </div>
                    )}
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="text-muted">Erstellt</span>
                      <span className="font-medium">
                        {formatDate(activity.created_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-[140px_1fr] gap-1">
                      <span className="text-muted">Aktualisiert</span>
                      <span className="font-medium">
                        {formatDate(activity.updated_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Lieferant */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <Building2 className="h-4 w-4" />
                      Lieferant / Supplier
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {activity.supplier ||
                    (activity as any).suppliers ? (
                      <>
                        <div className="grid grid-cols-[140px_1fr] gap-1">
                          <span className="text-muted">Name</span>
                          <span className="font-medium">
                            {activity.supplier?.name ??
                              (activity as any).suppliers?.name ??
                              '—'}
                          </span>
                        </div>
                        {(activity.supplier?.contact_name ??
                          (activity as any).suppliers?.contact_name) && (
                          <div className="grid grid-cols-[140px_1fr] gap-1">
                            <span className="text-muted">Kontakt</span>
                            <span className="font-medium">
                              {activity.supplier?.contact_name ??
                                (activity as any).suppliers?.contact_name}
                            </span>
                          </div>
                        )}
                        {(activity.supplier?.contact_email ??
                          (activity as any).suppliers?.contact_email) && (
                          <div className="grid grid-cols-[140px_1fr] gap-1">
                            <span className="text-muted">E-Mail</span>
                            <a
                              href={`mailto:${activity.supplier?.contact_email ?? (activity as any).suppliers?.contact_email}`}
                              className="font-medium text-accent hover:underline"
                            >
                              {activity.supplier?.contact_email ??
                                (activity as any).suppliers?.contact_email}
                            </a>
                          </div>
                        )}
                        {(activity.supplier?.phone ??
                          (activity as any).suppliers?.phone) && (
                          <div className="grid grid-cols-[140px_1fr] gap-1">
                            <span className="text-muted">Telefon</span>
                            <span className="font-medium">
                              {activity.supplier?.phone ??
                                (activity as any).suppliers?.phone}
                            </span>
                          </div>
                        )}
                        <div className="mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              onNavigate(
                                `/suppliers/${activity.supplier_id}`
                              )
                            }
                          >
                            <ExternalLink className="h-3 w-3" />
                            Supplier-Seite
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-muted italic">
                          Kein Lieferant zugewiesen.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openEditDialog}
                        >
                          <Plus className="h-3 w-3" />
                          Lieferant zuweisen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Budget-Übersicht */}
                <Card className={detailCard('lg:col-span-2')}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <BarChart3 className="h-4 w-4" />
                      Budget-Übersicht
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-xs text-muted">
                          Budget (geplant / tatsächlich)
                        </p>
                        <p className="text-lg font-semibold">
                          {formatCurrency(activity.budget_actual)}{' '}
                          <span className="text-sm font-normal text-muted">
                            / {formatCurrency(activity.budget_planned)}
                          </span>
                        </p>
                        <Progress value={budgetPct} className="h-1.5" />
                        <p className="text-xs text-muted">
                          {budgetPct}% ausgeschöpft
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted">
                          Lieferantenbudget
                        </p>
                        <p className="text-lg font-semibold">
                          {formatCurrency(activity.budget_supplier)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted">
                          Stunden (geplant / tatsächlich)
                        </p>
                        <p className="text-lg font-semibold">
                          {activity.hours_actual}h{' '}
                          <span className="text-sm font-normal text-muted">
                            / {activity.hours_planned}h
                          </span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── Tab: Zielsetzung ── */}
            <TabsContent value="goal" className="m-0 p-6">
              <div className="mx-auto max-w-3xl space-y-6">
                {/* Info-Banner */}
                <div className="flex items-start gap-3 rounded-lg border border-accent/30 bg-accent-soft p-4">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <p className="text-sm text-accent">
                    Diese Informationen erscheinen im PDF-Export und werden für
                    Förderanträge an Lieferanten verwendet.
                  </p>
                </div>

                {/* Zielsetzung */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <Target className="h-4 w-4" />
                      Zielsetzung
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted">
                      Was soll mit dieser Aktivität erreicht werden? Welche
                      konkreten Ziele verfolgen wir?
                    </p>
                    <AITextarea
                      value={goalDraft}
                      onChange={(e) => setGoalDraft(e.target.value)}
                      onValueChange={setGoalDraft}
                      rows={8}
                      placeholder="Zielsetzung beschreiben…"
                      aiFieldLabel="Zielsetzung der Aktivität"
                      aiContext={`${activityAiContext} Feld-Fokus: Was soll mit dieser Massnahme konkret erreicht werden — messbar oder klar abgrenzbar; keine vagen Slogans.`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSaveGoal}
                        disabled={savingGoal}
                      >
                        {savingGoal ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Zielgruppe */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <Users className="h-4 w-4" />
                      Zielgruppe
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted">
                      Welche Zielgruppe soll angesprochen werden? (Branche,
                      Unternehmensgrösse, Persona…)
                    </p>
                    <AITextarea
                      value={targetAudienceDraft}
                      onChange={(e) => setTargetAudienceDraft(e.target.value)}
                      onValueChange={setTargetAudienceDraft}
                      rows={4}
                      placeholder="Zielgruppe beschreiben…"
                      aiFieldLabel="Zielgruppe der Aktivität"
                      aiContext={`${activityAiContext} Feld-Fokus: Segmentierung (Rolle, Branche, Firmengrösse, Region, Entscheidungsniveau); keine generischen Persona-Klischees. Bezug zur Zielsetzung: ${goalDraft ? goalDraft.slice(0, 400) : '(noch leer)'}`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSaveTargetAudience}
                        disabled={savingTargetAudience}
                      >
                        {savingTargetAudience ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── Tab: Durchführung ── */}
            <TabsContent value="execution" className="m-0 p-6">
              <div className="mx-auto max-w-3xl space-y-6">
                {/* Info-Banner */}
                <div className="flex items-start gap-3 rounded-lg border border-accent/30 bg-accent-soft p-4">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <p className="text-sm text-accent">
                    Der Durchführungsplan erscheint im PDF-Export und gibt
                    Lieferanten einen klaren Überblick über die geplante
                    Umsetzung.
                  </p>
                </div>

                {/* Durchführungsplan */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <Cog className="h-4 w-4" />
                      Durchführungsplan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted">
                      Wie wird diese Aktivität konkret umgesetzt? Welche
                      Schritte, Kanäle und Ressourcen sind geplant?
                    </p>
                    <AITextarea
                      value={executionPlanDraft}
                      onChange={(e) => setExecutionPlanDraft(e.target.value)}
                      onValueChange={setExecutionPlanDraft}
                      rows={10}
                      placeholder="Durchführungsplan beschreiben…"
                      aiFieldLabel="Durchführungsplan der Aktivität"
                      aiContext={`${activityAiContext} Feld-Fokus: Konkrete Schritte, Kanäle, Lieferleistung und Zeitpunkte — umsetzbar für Lieferanten, keine Prozesstheorie.`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSaveExecution}
                        disabled={savingExecution}
                      >
                        {savingExecution ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Beschreibung */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <FileText className="h-4 w-4" />
                      Beschreibung
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted">
                      Allgemeine Beschreibung der Aktivität
                    </p>
                    <AITextarea
                      value={descriptionDraft}
                      onChange={(e) => setDescriptionDraft(e.target.value)}
                      onValueChange={setDescriptionDraft}
                      rows={5}
                      placeholder="Beschreibung eingeben…"
                      aiFieldLabel="Aktivitäts-Beschreibung"
                      aiContext={`${activityAiContext} Feld-Fokus: Kurze, klare Beschreibung der Massnahme im Kontext der Aktivität.`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={handleSaveDescription}
                        disabled={savingDescription}
                      >
                        {savingDescription ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Speichern
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── Tab: Checkliste ── */}
            <TabsContent value="checklist" className="m-0 p-6">
              <div className="mx-auto max-w-2xl">
                <Card className={detailCard()}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                        <ClipboardList className="h-4 w-4" />
                        Checkliste
                      </CardTitle>
                      {checklistTotal > 0 && (
                        <span className="text-sm text-muted">
                          {checklistDone} / {checklistTotal} erledigt
                        </span>
                      )}
                    </div>
                    {checklistTotal > 0 && (
                      <Progress
                        value={
                          checklistTotal > 0
                            ? Math.round((checklistDone / checklistTotal) * 100)
                            : 0
                        }
                        className="h-1.5"
                      />
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {activity.checklists?.length ? (
                      <ul className="space-y-2">
                        {activity.checklists
                          .slice()
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((item) => (
                            <li
                              key={item.id}
                              className="flex items-center gap-3 rounded-md border border-border p-2.5"
                            >
                              <Checkbox
                                checked={item.is_completed}
                                onCheckedChange={() =>
                                  handleChecklistToggle(item)
                                }
                              />
                              <span
                                className={cn(
                                  'flex-1 text-sm',
                                  item.is_completed &&
                                    'text-muted line-through'
                                )}
                              >
                                {item.title}
                              </span>
                              {item.is_completed ? (
                                <CheckCircle2 className="h-4 w-4 text-success" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted" />
                              )}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted italic">
                        Noch keine Checklistenpunkte.
                      </p>
                    )}

                    {/* Neuer Punkt */}
                    <div className="flex gap-2 pt-2">
                      <Input
                        placeholder="Neuer Punkt…"
                        value={newChecklistTitle}
                        onChange={(e) => setNewChecklistTitle(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && handleAddChecklistItem()
                        }
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        disabled={
                          !newChecklistTitle.trim() || addingChecklist
                        }
                        onClick={handleAddChecklistItem}
                      >
                        {addingChecklist ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── Tab: Kommentare ── */}
            <TabsContent value="comments" className="m-0 p-6">
              <div className="mx-auto max-w-2xl space-y-4">
                {activity.comments?.length ? (
                  <ul className="space-y-3">
                    {[...activity.comments]
                      .sort(
                        (a, b) =>
                          new Date(a.created_at).getTime() -
                          new Date(b.created_at).getTime()
                      )
                      .map((c) => (
                        <li
                          key={c.id}
                          className="flex gap-3 rounded-lg border border-border p-4"
                        >
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarFallback className="text-xs">
                              {c.user ? getInitials(c.user.full_name) : '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <p className="text-sm font-medium">
                                {c.user?.full_name ?? 'Unbekannt'}
                              </p>
                              <p className="text-xs text-muted">
                                {formatDate(c.created_at, 'long')}
                              </p>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap text-sm">
                              {c.content}
                            </p>
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-center text-sm text-muted italic">
                    Noch keine Kommentare.
                  </p>
                )}

                <Card className={detailCard()}>
                  <CardContent className="space-y-3 pt-4">
                    <AITextarea
                      placeholder="Kommentar hinzufügen…"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onValueChange={setNewComment}
                      rows={3}
                      aiFieldLabel="Aktivitäts-Kommentar"
                      aiContext={`Kommentar zur Aktivität: ${activity.title}`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        disabled={!newComment.trim() || addingComment}
                        onClick={handleAddComment}
                      >
                        {addingComment ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Kommentar senden
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── Tab: KPIs & Budget ── */}
            <TabsContent value="kpis" className="m-0 p-6">
              <div className="mx-auto max-w-3xl space-y-6">
                {/* Budget-Tabelle */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                        <BarChart3 className="h-4 w-4" />
                        Budget & Stunden
                      </CardTitle>
                      {!kpiEditing && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setKpiEditing(true)}
                        >
                          <Pencil className="h-4 w-4" />
                          Ist-Werte erfassen
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="pb-2 text-left font-medium text-muted">
                            Kategorie
                          </th>
                          <th className="pb-2 text-right font-medium text-muted">
                            Geplant
                          </th>
                          <th className="pb-2 text-right font-medium text-muted">
                            Tatsächlich
                          </th>
                          <th className="pb-2 text-right font-medium text-muted">
                            Differenz
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="py-2 text-muted">
                            Budget gesamt
                          </td>
                          <td className="py-2 text-right">
                            {formatCurrency(activity.budget_planned)}
                          </td>
                          <td className="py-2 text-right">
                            {kpiEditing ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="ml-auto h-7 w-28 text-right text-sm"
                                value={kpiForm.budget_actual}
                                onChange={(e) =>
                                  setKpiForm((f) => ({
                                    ...f,
                                    budget_actual: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              formatCurrency(activity.budget_actual)
                            )}
                          </td>
                          <td
                            className={cn(
                              'py-2 text-right font-medium',
                              activity.budget_actual > activity.budget_planned
                                ? 'text-danger'
                                : 'text-success'
                            )}
                          >
                            {formatCurrency(
                              activity.budget_actual - activity.budget_planned
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 text-muted">
                            Lieferantenanteil
                          </td>
                          <td className="py-2 text-right">
                            {formatCurrency(activity.budget_supplier)}
                          </td>
                          <td className="py-2 text-right">—</td>
                          <td className="py-2 text-right">—</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-muted">
                            Stunden
                          </td>
                          <td className="py-2 text-right">
                            {activity.hours_planned}h
                          </td>
                          <td className="py-2 text-right">
                            {kpiEditing ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                className="ml-auto h-7 w-24 text-right text-sm"
                                value={kpiForm.hours_actual}
                                onChange={(e) =>
                                  setKpiForm((f) => ({
                                    ...f,
                                    hours_actual: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              `${activity.hours_actual}h`
                            )}
                          </td>
                          <td
                            className={cn(
                              'py-2 text-right font-medium',
                              activity.hours_actual > activity.hours_planned
                                ? 'text-danger'
                                : 'text-success'
                            )}
                          >
                            {`${activity.hours_actual - activity.hours_planned >= 0 ? '+' : ''}${activity.hours_actual - activity.hours_planned}h`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* KPI-Grid */}
                <Card className={detailCard()}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                      <Target className="h-4 w-4" />
                      Kennzahlen (KPIs)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-3">
                      {/* Leads */}
                      <div className="rounded-lg border border-border p-3">
                        <p className="text-xs text-muted">Leads</p>
                        <div className="mt-1 flex items-baseline gap-1">
                          {kpiEditing ? (
                            <Input
                              type="number"
                              min="0"
                              className="h-7 w-24 text-sm"
                              value={kpiForm.actual_leads}
                              onChange={(e) =>
                                setKpiForm((f) => ({
                                  ...f,
                                  actual_leads: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <span className="text-2xl font-bold">
                              {activity.actual_leads}
                            </span>
                          )}
                          <span className="text-sm text-muted">
                            / {activity.expected_leads} Ziel
                          </span>
                        </div>
                        {activity.expected_leads > 0 && (
                          <>
                            <Progress value={goalPct} className="mt-2 h-1.5" />
                            <p className="mt-1 text-xs text-muted">
                              {Math.min(
                                100,
                                Math.round(
                                  (activity.actual_leads /
                                    activity.expected_leads) *
                                    100
                                )
                              )}
                              % Zielerreichung
                            </p>
                          </>
                        )}
                      </div>

                      {/* Conversions */}
                      <div className="rounded-lg border border-border p-3">
                        <p className="text-xs text-muted">Conversions</p>
                        <div className="mt-1 flex items-baseline gap-1">
                          {kpiEditing ? (
                            <Input
                              type="number"
                              min="0"
                              className="h-7 w-24 text-sm"
                              value={kpiForm.actual_conversions}
                              onChange={(e) =>
                                setKpiForm((f) => ({
                                  ...f,
                                  actual_conversions: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <span className="text-2xl font-bold">
                              {activity.actual_conversions}
                            </span>
                          )}
                          <span className="text-sm text-muted">
                            / {activity.expected_conversions} Ziel
                          </span>
                        </div>
                        {activity.expected_conversions > 0 && (
                          <>
                            <Progress
                              value={Math.min(
                                100,
                                Math.round(
                                  (activity.actual_conversions /
                                    activity.expected_conversions) *
                                    100
                                )
                              )}
                              className="mt-2 h-1.5"
                            />
                            <p className="mt-1 text-xs text-muted">
                              {Math.min(
                                100,
                                Math.round(
                                  (activity.actual_conversions /
                                    activity.expected_conversions) *
                                    100
                                )
                              )}
                              % Zielerreichung
                            </p>
                          </>
                        )}
                      </div>

                      {/* Revenue */}
                      <div className="rounded-lg border border-border p-3">
                        <p className="text-xs text-muted">Umsatz</p>
                        <div className="mt-1 flex flex-col">
                          {kpiEditing ? (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="h-7 w-full text-sm"
                              value={kpiForm.actual_revenue}
                              onChange={(e) =>
                                setKpiForm((f) => ({
                                  ...f,
                                  actual_revenue: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <span className="text-xl font-bold">
                              {formatCurrency(activity.actual_revenue)}
                            </span>
                          )}
                          <span className="mt-0.5 text-xs text-muted">
                            Ziel: {formatCurrency(activity.expected_revenue)}
                          </span>
                        </div>
                        {activity.expected_revenue > 0 && (
                          <>
                            <Progress
                              value={Math.min(
                                100,
                                Math.round(
                                  (activity.actual_revenue /
                                    activity.expected_revenue) *
                                    100
                                )
                              )}
                              className="mt-2 h-1.5"
                            />
                            <p className="mt-1 text-xs text-muted">
                              {Math.min(
                                100,
                                Math.round(
                                  (activity.actual_revenue /
                                    activity.expected_revenue) *
                                    100
                                )
                              )}
                              % Zielerreichung
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {kpiEditing && (
                      <div className="mt-4 flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setKpiEditing(false)}
                        >
                          <X className="h-4 w-4" />
                          Abbrechen
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveKpis}
                          disabled={savingKpis}
                        >
                          {savingKpis ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          KPIs speichern
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </div>

      {/* ── Edit-Dialog ── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aktivität bearbeiten</DialogTitle>
            <DialogDescription>
              Alle Felder anpassen und speichern.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {editError && (
              <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {editError}
              </p>
            )}

            {/* Titel */}
            <div className="space-y-1">
              <Label htmlFor="edit-title">Titel *</Label>
              <AIInput
                id="edit-title"
                value={editForm.title}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, title: e.target.value }))
                }
                onValueChange={(v) =>
                  setEditForm((f) => ({ ...f, title: v }))
                }
                placeholder="Titel der Aktivität"
                aiFieldLabel="Aktivitäts-Titel"
                aiContext={`Marketing-Aktivität, Typ: ${activity?.type ?? ''}`}
              />
            </div>

            {/* Typ / Status / Priorität */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Typ</Label>
                <Select
                  value={editForm.type}
                  onValueChange={(v) =>
                    setEditForm((f) => ({ ...f, type: v as ActivityType }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(v) =>
                    setEditForm((f) => ({
                      ...f,
                      status: v as ActivityStatus,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Priorität</Label>
                <Select
                  value={editForm.priority}
                  onValueChange={(v) =>
                    setEditForm((f) => ({
                      ...f,
                      priority: v as PriorityLevel,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase / Kampagne */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Phase / Quartal</Label>
                <Select
                  value={editForm.phase_id ?? 'none'}
                  onValueChange={(v) =>
                    setEditForm((f) => ({
                      ...f,
                      phase_id: v === 'none' ? null : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keine Phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Phase</SelectItem>
                    {phases.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Kampagne</Label>
                <Select
                  value={editForm.campaign_id ?? 'none'}
                  onValueChange={(v) =>
                    setEditForm((f) => ({
                      ...f,
                      campaign_id: v === 'none' ? null : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keine Kampagne" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Kampagne</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lieferant */}
            <div className="space-y-1">
              <Label>Lieferant</Label>
              <Select
                value={editForm.supplier_id ?? 'none'}
                onValueChange={(v) =>
                  setEditForm((f) => ({
                    ...f,
                    supplier_id: v === 'none' ? null : v,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kein Lieferant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Lieferant</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.contact_name && (
                        <span className="ml-1 text-xs text-muted">
                          ({s.contact_name})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Datum */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-start">Startdatum</Label>
                <Input
                  id="edit-start"
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      start_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-end">Enddatum</Label>
                <Input
                  id="edit-end"
                  type="date"
                  value={editForm.end_date}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, end_date: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Budget */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-budget">Budget geplant (CHF)</Label>
                <Input
                  id="edit-budget"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.budget_planned}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      budget_planned: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-budget-supplier">
                  Lieferantenbudget (CHF)
                </Label>
                <Input
                  id="edit-budget-supplier"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.budget_supplier}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      budget_supplier: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-hours">Stunden geplant</Label>
                <Input
                  id="edit-hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={editForm.hours_planned}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      hours_planned: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* KPI Planzahlen */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="edit-leads">Leads (Ziel)</Label>
                <Input
                  id="edit-leads"
                  type="number"
                  min="0"
                  value={editForm.expected_leads}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      expected_leads: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-conv">Conversions (Ziel)</Label>
                <Input
                  id="edit-conv"
                  type="number"
                  min="0"
                  value={editForm.expected_conversions}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      expected_conversions: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-rev">Umsatz Ziel (CHF)</Label>
                <Input
                  id="edit-rev"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.expected_revenue}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      expected_revenue: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* Externe URL */}
            <div className="space-y-1">
              <Label htmlFor="edit-link">Externe URL</Label>
              <Input
                id="edit-link"
                type="url"
                placeholder="https://…"
                value={editForm.external_link}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    external_link: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={saving}
            >
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
